/* ==========================================================================
   3rd Millennium Trade Theme — Custom JavaScript
   Web Components following Dawn's architecture pattern
   ========================================================================== */

/* ---------- Required Accessories Component ---------- */
class RequiredAccessories extends HTMLElement {
  constructor() {
    super();
    this.addAllBtn = this.querySelector('[data-add-bundle]');
    this.totalDisplay = this.querySelector('[data-bundle-total]');
    this.checkboxes = this.querySelectorAll('input[name="accessory[]"]');
    this.variantSelects = this.querySelectorAll('.accessory-card__variant-select');
  }

  connectedCallback() {
    this.addAllBtn?.addEventListener('click', this.handleAddBundle.bind(this));
    this.checkboxes.forEach(cb => cb.addEventListener('change', this.updateTotal.bind(this)));
    this.variantSelects.forEach(sel => sel.addEventListener('change', this.updateTotal.bind(this)));
    this.updateTotal();
  }

  getSelectedItems() {
    const checked = this.querySelectorAll('input[name="accessory[]"]:checked');
    return Array.from(checked).map(cb => {
      const card = cb.closest('.accessory-card');
      const variantSelect = card.querySelector('.accessory-card__variant-select');
      return {
        id: parseInt(variantSelect ? variantSelect.value : cb.value),
        quantity: 1
      };
    });
  }

  updateTotal() {
    const mainPriceEl = document.querySelector('[data-product-price]');
    const mainPrice = mainPriceEl ? parseInt(mainPriceEl.dataset.productPrice || 0) : 0;
    let accessoryTotal = 0;

    this.querySelectorAll('input[name="accessory[]"]:checked').forEach(cb => {
      const card = cb.closest('.accessory-card');
      const variantSelect = card.querySelector('.accessory-card__variant-select');
      const price = variantSelect
        ? parseInt(variantSelect.selectedOptions[0]?.dataset.price || 0)
        : parseInt(cb.dataset.price || 0);
      accessoryTotal += price;
    });

    if (this.totalDisplay && window.Shopify?.formatMoney) {
      this.totalDisplay.textContent = Shopify.formatMoney(mainPrice + accessoryTotal);
    }

    // Update count on button
    const count = this.querySelectorAll('input[name="accessory[]"]:checked').length;
    if (this.addAllBtn) {
      this.addAllBtn.textContent = count > 0
        ? `Add all ${count + 1} items to cart`
        : 'Add to cart';
    }
  }

  async handleAddBundle() {
    // Get the main product variant from the page's product form
    const mainForm = document.querySelector('form[action="/cart/add"] [name="id"]')
      || document.querySelector('product-form [name="id"]');
    const mainVariantId = mainForm ? parseInt(mainForm.value) : null;

    const items = [];
    if (mainVariantId) items.push({ id: mainVariantId, quantity: 1 });
    items.push(...this.getSelectedItems());

    if (items.length === 0) return;

    this.addAllBtn.disabled = true;
    const originalText = this.addAllBtn.textContent;
    this.addAllBtn.textContent = 'Adding...';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      // Trigger cart update via Dawn's pub/sub system
      if (window.publish) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'required-accessories' });
      }

      // Also dispatch standard event for cart drawer
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      this.addAllBtn.textContent = 'Added!';
      setTimeout(() => {
        this.addAllBtn.textContent = originalText;
        this.addAllBtn.disabled = false;
      }, 1500);

      // Open cart drawer if available
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        // Re-fetch cart drawer contents
        fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer`)
          .then(r => r.json())
          .then(sections => {
            const parser = new DOMParser();
            for (const [id, html] of Object.entries(sections)) {
              const doc = parser.parseFromString(html, 'text/html');
              const newContent = doc.querySelector('.drawer__inner');
              const target = cartDrawer.querySelector('.drawer__inner');
              if (newContent && target) target.innerHTML = newContent.innerHTML;
            }
            cartDrawer.open();
          });
      }
    } catch (err) {
      console.error('Bundle add failed:', err);
      this.addAllBtn.textContent = 'Error - try again';
      setTimeout(() => {
        this.addAllBtn.textContent = originalText;
        this.addAllBtn.disabled = false;
      }, 2000);
    }
  }
}
customElements.define('required-accessories', RequiredAccessories);


/* ---------- Sticky Mobile ATC Component ---------- */
class StickyAtc extends HTMLElement {
  constructor() {
    super();
    this.mainAtc = null;
    this.observer = null;
  }

  connectedCallback() {
    // Find the main add-to-cart button on the page
    this.mainAtc = document.querySelector('.product-form__submit')
      || document.querySelector('[name="add"]');

    if (!this.mainAtc) return;

    // Use IntersectionObserver to detect when main ATC leaves viewport
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.classList.remove('is-visible');
            document.body.classList.remove('sticky-atc-active');
          } else {
            this.classList.add('is-visible');
            document.body.classList.add('sticky-atc-active');
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -60px 0px' }
    );

    this.observer.observe(this.mainAtc);

    // Handle click on sticky button
    const btn = this.querySelector('.sticky-atc__btn');
    btn?.addEventListener('click', () => {
      // Scroll to and click the real ATC button
      this.mainAtc.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => this.mainAtc.click(), 400);
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}
customElements.define('sticky-atc', StickyAtc);


/* ---------- Collection View Toggle Component ---------- */
class CollectionViewToggle extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById(this.dataset.gridId);
    this.buttons = this.querySelectorAll('.collection-view-toggle__btn');

    // Restore saved preference
    const saved = localStorage.getItem('collection-view') || 'grid';
    this.setView(saved);

    this.buttons.forEach(btn => {
      btn.addEventListener('click', () => this.setView(btn.dataset.view));
    });
  }

  setView(view) {
    if (!this.grid) return;

    this.buttons.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.view === view);
    });

    if (view === 'list') {
      this.grid.classList.add('product-grid--list');
    } else {
      this.grid.classList.remove('product-grid--list');
    }

    localStorage.setItem('collection-view', view);
  }
}
customElements.define('collection-view-toggle', CollectionViewToggle);


/* ---------- Trade Quick Add (multi-item from collection page) ---------- */
class TradeQuickAdd extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.btn = this.querySelector('.card-quick-add-trade__btn');
    this.qtyInput = this.querySelector('.card-quick-add-trade__qty');
    this.variantSelect = this.querySelector('.card-quick-add-trade__variant');

    this.btn?.addEventListener('click', this.handleAdd.bind(this));
  }

  async handleAdd(e) {
    e.preventDefault();
    e.stopPropagation();

    const variantId = this.variantSelect
      ? parseInt(this.variantSelect.value)
      : parseInt(this.dataset.variantId);
    const quantity = parseInt(this.qtyInput?.value || 1);

    if (!variantId || quantity < 1) return;

    this.btn.disabled = true;
    const originalText = this.btn.textContent;
    this.btn.textContent = '...';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] })
      });

      if (!response.ok) throw new Error('Add failed');

      // Trigger cart update
      if (window.publish && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'trade-quick-add' });
      }

      this.btn.textContent = '\u2713';
      setTimeout(() => {
        this.btn.textContent = originalText;
        this.btn.disabled = false;
      }, 1000);

      // Open cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer`)
          .then(r => r.json())
          .then(sections => {
            const parser = new DOMParser();
            for (const [id, html] of Object.entries(sections)) {
              const doc = parser.parseFromString(html, 'text/html');
              const newContent = doc.querySelector('.drawer__inner');
              const target = cartDrawer.querySelector('.drawer__inner');
              if (newContent && target) target.innerHTML = newContent.innerHTML;
            }
            cartDrawer.open();
          });
      }
    } catch (err) {
      console.error('Quick add failed:', err);
      this.btn.textContent = 'Error';
      setTimeout(() => {
        this.btn.textContent = originalText;
        this.btn.disabled = false;
      }, 1500);
    }
  }
}
customElements.define('trade-quick-add', TradeQuickAdd);
